import { Injectable } from '@angular/core';

import { ConfigParserService } from '../services/config-parser.service';

export interface MruConfig {
    activityEnabled: boolean;
    activityStrings: { [name: string]: string };
    addWacParams: boolean;
    analyticsCachingEnabled: boolean;
    analyticsEnabled: boolean;
    clickableFolderEnabled: boolean;
    copyLinkEnabled: boolean;
    deferFeedWrite: boolean;
    infiniteShowMoreEnabled: boolean;
    isNewLifecycleEventEnabled: boolean;
    isNewTimeWindowInHours: number;
    logThumbnailLoadSuccess: boolean;
    maxInitialListItems: number;
    maxTotalListItems: number;
    mobileShareAndActivityEnabled: boolean;
    mruActivityEnabled: boolean;
    mruNumItemsToRequest: number;
    mruPinEnabled: boolean;
    mruServiceRoot: string;
    newMruActions: boolean;
    numRecommendedToRequest: number;
    officeFeedApiRootPath: string;
    officeFeedControlEnabled: boolean;
    officeFeedFlightEnabled: boolean;
    officeFeedFlightName: string;
    officeFeedFlightControlName: string;
    officeFeedExperimentName: string;
    officeFeedExperimentControlName: string;
    officeFeedFlight2Name: string;
    officeFeedFlight2ControlName: string;
    officeFeedExperiment2Name: string;
    officeFeedExperiment2ControlName: string;
    officeFeedFlight3Name: string;
    officeFeedFlight3ControlName: string;
    officeFeedExperiment3Name: string;
    officeFeedExperiment3ControlName: string;
    officeFeedNewSchemaEnabled: string;
    oneDriveDiscoverViewId: string;
    oneDriveRecentViewId: string;
    oneDriveSharedViewId: string;
    onlyMruActivityEnabled: boolean;
    permissionsEnabled: boolean;
    recommendedApiDismissedDocumentsPath: string;
    recommendedB2ApiRootPath: string;
    recommendedB2ApiFilesPath: string;
    recommendedB2EndpointEnabled: boolean;
    recommendedCalloutEnabled: boolean;
    recommendedEnabled: boolean;
    recommendedFluidEnabled: boolean;
    recommendedMotionEnabled: boolean;
    recommendedNewCardsEnabled: boolean;
    recommendedPdfEnabled: boolean;
    recommendedRemoveItemEnabled: boolean;
    recommendedVisioEnabled: boolean;
    shareApiEndpoint: string;
    shareControlEnabled: boolean;
    shareControlCAPPopupEnabled: boolean;
    sharedAttachmentsEnabled: boolean;
    sharedWithMeV2Enabled: boolean;
    sharedWithMeViewEnabled: boolean;
    sharingStatusEnabled: boolean;
    showDownloadAction: boolean;
    showZeroExperience: boolean;
    silentS2SFlow: boolean;
    substrateApiEndpoint: string;
    supportedFileTypes: Array<string>;
    supportedFileTypesSwM: Array<string>;
    swayTransformedFromWordAttributionEnabled: boolean;
    tileViewEnabled: boolean;
    thumbnailSWEnabled: boolean;
    trendingAroundMeEnabled: boolean;
    webUrlMruConsumerEnabled: boolean;
    wordIconFontClass: string;
    excelIconFontClass: string;
    visioIconFontClass: string;
    powerPointIconFontClass: string;
    oneNoteIconFontClass: string;
    swayIconFontClass: string;
    formsIconFontClass: string;
    powerBIIconFontClass: string;
    streamIconFontClass: string;
    outlookIconFontClass: string;
    upcomingMeetingAttendeeDisplayMax: number;
    upcomingMeetingEnabled: boolean;
    useFeedS2SApi: boolean;
    myAnalyticsEnabled: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class MruConfigService {
    public readonly config: MruConfig;

    constructor(configParserService: ConfigParserService) {
        // Initialize to default values if there was failure to read config from page response
        // Note that default values are not meant to make MRU operational, in fact these are meant
        // to ensure that MRU related code will not result into any unhandled exceptions and the
        // rest of the application will function fine.
        this.config = configParserService.parse<MruConfig>('mruConfig') || {
            activityEnabled: false,
            activityStrings: {},
            addWacParams: false,
            analyticsCachingEnabled: false,
            analyticsEnabled: false,
            clickableFolderEnabled: false,
            copyLinkEnabled: false,
            deferFeedWrite: false,
            infiniteShowMoreEnabled: false,
            isNewLifecycleEventEnabled: false,
            isNewTimeWindowInHours: 0,
            logThumbnailLoadSuccess: false,
            maxInitialListItems: 0,
            maxTotalListItems: 0,
            mobileShareAndActivityEnabled: false,
            mruActivityEnabled: false,
            mruNumItemsToRequest: 0,
            mruPinEnabled: false,
            mruServiceRoot: null,
            newMruActions: false,
            numRecommendedToRequest: 0,
            officeFeedApiRootPath: '',
            officeFeedControlEnabled: false,
            officeFeedFlightEnabled: false,
            officeFeedFlightName: '',
            officeFeedFlightControlName: '',
            officeFeedExperimentName: '',
            officeFeedExperimentControlName: '',
            officeFeedFlight2Name: '',
            officeFeedFlight2ControlName: '',
            officeFeedExperiment2Name: '',
            officeFeedExperiment2ControlName: '',
            officeFeedFlight3Name: '',
            officeFeedFlight3ControlName: '',
            officeFeedExperiment3Name: '',
            officeFeedExperiment3ControlName: '',
            officeFeedNewSchemaEnabled: '',
            oneDriveDiscoverViewId: null,
            oneDriveRecentViewId: null,
            oneDriveSharedViewId: null,
            onlyMruActivityEnabled: false,
            permissionsEnabled: false,
            recommendedApiDismissedDocumentsPath: '',
            recommendedB2ApiRootPath: '',
            recommendedB2ApiFilesPath: '',
            recommendedB2EndpointEnabled: false,
            recommendedCalloutEnabled: false,
            recommendedEnabled: false,
            recommendedFluidEnabled: false,
            recommendedMotionEnabled: false,
            recommendedNewCardsEnabled: false,
            recommendedPdfEnabled: false,
            recommendedRemoveItemEnabled: false,
            recommendedVisioEnabled: false,
            shareApiEndpoint: null,
            shareControlEnabled: false,
            shareControlCAPPopupEnabled: false,
            sharedAttachmentsEnabled: false,
            sharedWithMeV2Enabled: false,
            sharedWithMeViewEnabled: false,
            sharingStatusEnabled: false,
            showDownloadAction: false,
            showZeroExperience: false,
            substrateApiEndpoint: null,
            supportedFileTypes: [],
            supportedFileTypesSwM: [],
            swayTransformedFromWordAttributionEnabled: false,
            tileViewEnabled: false,
            thumbnailSWEnabled: false,
            trendingAroundMeEnabled: false,
            webUrlMruConsumerEnabled: false,
            wordIconFontClass: '',
            excelIconFontClass: '',
            visioIconFontClass: '',
            powerPointIconFontClass: '',
            oneNoteIconFontClass: '',
            swayIconFontClass: '',
            formsIconFontClass: '',
            powerBIIconFontClass: '',
            streamIconFontClass: '',
            outlookIconFontClass: '',
            silentS2SFlow: false,
            upcomingMeetingAttendeeDisplayMax: 0,
            upcomingMeetingEnabled: false,
            useFeedS2SApi: false,
            myAnalyticsEnabled: false
        };
    }
}
